/**
 * React hook for managing notifications
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { DAGStore, Identity } from '@amorphous/core';
import {
  type Notification,
  type CreateNotificationInput,
  createNotification,
  getAllNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  notificationExists,
} from '../storage/notifications';

interface UseNotificationsResult {
  /** All notifications */
  notifications: Notification[];
  /** Number of unread notifications */
  unreadCount: number;
  /** Loading state */
  loading: boolean;
  /** Add a new notification */
  addNotification: (input: CreateNotificationInput) => Promise<Notification>;
  /** Mark a notification as read */
  markRead: (id: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllRead: () => Promise<void>;
  /** Delete a notification */
  remove: (id: string) => Promise<void>;
  /** Clear all notifications */
  clearAll: () => Promise<void>;
  /** Refresh notifications from storage */
  refresh: () => Promise<void>;
}

interface UseNotificationsOptions {
  /** DAG store for monitoring new objects */
  store: DAGStore | null;
  /** Current user's identity */
  identity: Identity | null;
  /** Get display name for a pubkey */
  getDisplayName?: (pubkey: string) => string;
}

export function useNotifications({
  store,
  identity,
  getDisplayName,
}: UseNotificationsOptions): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Track known objects to detect new ones
  const knownObjectsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [all, count] = await Promise.all([
        getAllNotifications(),
        getUnreadCount(),
      ]);
      setNotifications(all);
      setUnreadCount(count);
    } catch (err) {
      console.error('[Notifications] Failed to refresh:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notifications on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Monitor for new objects that should trigger notifications
  useEffect(() => {
    if (!store || !identity) return;

    const checkForNotifications = async () => {
      try {
        const myPubkey = identity.publicKey;
        const allObjects = await store.getAll();

        // On initial load, just populate known objects without creating notifications
        if (!initialLoadRef.current) {
          allObjects.forEach(obj => knownObjectsRef.current.add(obj.hash));
          initialLoadRef.current = true;
          return;
        }

        // Check for new objects
        for (const obj of allObjects) {
          if (knownObjectsRef.current.has(obj.hash)) continue;
          knownObjectsRef.current.add(obj.hash);

          // Skip our own objects
          if (obj.creator === myPubkey) continue;

          // Check for friend requests (relationship objects where we're the target)
          if (obj.type === 'relationship') {
            const content = obj.content as { target: string; type: string };
            if (content.target === myPubkey && content.type === 'friend') {
              // Check if we already have this notification
              const exists = await notificationExists('friend_request', obj.creator);
              if (!exists) {
                const displayName = getDisplayName?.(obj.creator) || obj.creator.slice(0, 8) + '...';
                await createNotification({
                  type: 'friend_request',
                  title: 'New friend request',
                  message: `${displayName} added you as a friend`,
                  fromPubkey: obj.creator,
                });
                refresh();
              }
            }
          }

          // Check for replies to our posts
          if (obj.type === 'article' && obj.parents?.length) {
            const parentHash = obj.parents[0];
            const parent = await store.get(parentHash);
            if (parent && parent.creator === myPubkey) {
              // Someone replied to our post
              const exists = await notificationExists('reply', obj.creator, obj.hash);
              if (!exists) {
                const displayName = getDisplayName?.(obj.creator) || obj.creator.slice(0, 8) + '...';
                const parentTitle = (parent.content as { title?: string })?.title || 'your post';
                await createNotification({
                  type: 'reply',
                  title: 'New reply',
                  message: `${displayName} replied to "${parentTitle.slice(0, 30)}${parentTitle.length > 30 ? '...' : ''}"`,
                  fromPubkey: obj.creator,
                  relatedHash: obj.hash,
                });
                refresh();
              }
            }
          }

          // Check for votes on our posts (optional - can be noisy)
          // Uncomment if you want vote notifications
          /*
          if (obj.type === 'vote') {
            const content = obj.content as { target: string; value: number };
            const target = await store.get(content.target);
            if (target && target.creator === myPubkey && content.value === 1) {
              const exists = await notificationExists('vote', obj.creator, obj.hash);
              if (!exists) {
                const displayName = getDisplayName?.(obj.creator) || obj.creator.slice(0, 8) + '...';
                await createNotification({
                  type: 'vote',
                  title: 'New upvote',
                  message: `${displayName} upvoted your post`,
                  fromPubkey: obj.creator,
                  relatedHash: content.target,
                });
                refresh();
              }
            }
          }
          */
        }
      } catch (err) {
        console.error('[Notifications] Error checking for notifications:', err);
      }
    };

    // Check periodically
    checkForNotifications();
    const interval = setInterval(checkForNotifications, 10000); // Every 10 seconds

    return () => clearInterval(interval);
  }, [store, identity, getDisplayName, refresh]);

  const addNotification = useCallback(async (input: CreateNotificationInput) => {
    const notification = await createNotification(input);
    await refresh();
    return notification;
  }, [refresh]);

  const markRead = useCallback(async (id: string) => {
    await markAsRead(id);
    await refresh();
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    await markAllAsRead();
    await refresh();
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    await deleteNotification(id);
    await refresh();
  }, [refresh]);

  const clearAll = useCallback(async () => {
    await clearAllNotifications();
    await refresh();
  }, [refresh]);

  return {
    notifications,
    unreadCount,
    loading,
    addNotification,
    markRead,
    markAllRead,
    remove,
    clearAll,
    refresh,
  };
}
